import React, { useState, useEffect, useRef } from "react";
import "./ChatWidget.css";
import { db } from "./firebase";
import {
  collection,
  addDoc,
  doc,
  onSnapshot,
  orderBy,
  query,
  where,
  limit,
  serverTimestamp,
  updateDoc,
  increment,
  getDoc,
  setDoc,
} from "firebase/firestore";

const STORAGE_KEY = "mrtruck_chatId";

const ChatIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
  </svg>
);

const CloseIcon = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    <path d="M1 1l12 12M13 1L1 13"/>
  </svg>
);

const SendIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="22" y1="2" x2="11" y2="13"/>
    <polygon points="22 2 15 22 11 13 2 9 22 2"/>
  </svg>
);

function formatTime(timestamp) {
  if (!timestamp) return "";
  const d = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
  return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

export default function ChatWidget() {
  const [isOpen, setIsOpen]       = useState(false);
  const [step, setStep]           = useState("welcome"); // 'welcome' | 'chat'
  const [name, setName]           = useState("");
  const [email, setEmail]         = useState("");
  const [chatId, setChatId]       = useState(null);
  const [messages, setMessages]   = useState([]);
  const [newMsg, setNewMsg]       = useState("");
  const [sending, setSending]     = useState(false);
  const [starting, setStarting]   = useState(false);
  const [chatStatus, setChatStatus] = useState("open");
  const [customerUnread, setCustomerUnread] = useState(0);
  const [adminOnline, setAdminOnline] = useState(false);

  const messagesEndRef = useRef(null);
  const textareaRef    = useRef(null);

  // ── Monitor admin presence via onlineStatuses collection ──────────────
  useEffect(() => {
    const q = query(
      collection(db, "onlineStatuses"),
      where("role", "==", "admin"),
      limit(1)
    );
    const unsubscribe = onSnapshot(
      q,
      (snap) => setAdminOnline(!snap.empty && snap.docs[0].data().isOnline === true),
      () => setAdminOnline(false)
    );
    return unsubscribe;
  }, []);

  // ── Restore session from localStorage ──────────────
  useEffect(() => {
    const savedId = localStorage.getItem(STORAGE_KEY);
    if (!savedId) return;

    // Verify the chat still exists
    getDoc(doc(db, "chats", savedId)).then((snap) => {
      if (snap.exists()) {
        setChatId(savedId);
        setStep("chat");
        setChatStatus(snap.data().status || "open");
        const data = snap.data();
        if (data.customerName) setName(data.customerName);
      } else {
        localStorage.removeItem(STORAGE_KEY);
      }
    }).catch(() => localStorage.removeItem(STORAGE_KEY));
  }, []);

  // ── Subscribe to messages ───────────────────────────
  useEffect(() => {
    if (!chatId) return;

    const msgsQ = query(
      collection(db, "chats", chatId, "messages"),
      orderBy("createdAt", "asc")
    );

    const unsubMsgs = onSnapshot(msgsQ, (snap) => {
      setMessages(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    });

    // Subscribe to the chat doc for status + unread
    const unsubChat = onSnapshot(doc(db, "chats", chatId), (snap) => {
      if (snap.exists()) {
        setChatStatus(snap.data().status || "open");
        const unread = snap.data().customerUnread || 0;
        setCustomerUnread(unread);
        // Clear unread when panel is open
        if (isOpen && unread > 0) {
          updateDoc(doc(db, "chats", chatId), { customerUnread: 0 }).catch(() => {});
        }
      }
    });

    return () => { unsubMsgs(); unsubChat(); };
  }, [chatId, isOpen]);

  // ── Scroll to latest message ────────────────────────
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // ── Clear unread when opening ───────────────────────
  useEffect(() => {
    if (isOpen && chatId && customerUnread > 0) {
      updateDoc(doc(db, "chats", chatId), { customerUnread: 0 }).catch(() => {});
    }
  }, [isOpen, chatId, customerUnread]);

  // ── Start a new chat session ────────────────────────
  const handleStart = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    setStarting(true);

    try {
      const chatRef = doc(collection(db, "chats"));
      await setDoc(chatRef, {
        customerName: name.trim(),
        customerEmail: email.trim(),
        status: "open",
        createdAt: serverTimestamp(),
        lastMessage: "Chat started",
        lastMessageAt: serverTimestamp(),
        adminUnread: 1,
        customerUnread: 0,
      });

      // Send an automated first message
      await addDoc(collection(db, "chats", chatRef.id, "messages"), {
        text: `Hi ${name.trim()}, welcome to Mr Truck support. How can we help you today?`,
        sender: "admin",
        createdAt: serverTimestamp(),
      });

      localStorage.setItem(STORAGE_KEY, chatRef.id);
      setChatId(chatRef.id);
      setStep("chat");
    } catch (err) {
      console.error("Failed to start chat:", err);
      alert("Could not start chat. Please try again.");
    }
    setStarting(false);
  };

  // ── Send a message ──────────────────────────────────
  const handleSend = async () => {
    const text = newMsg.trim();
    if (!text || sending || !chatId || chatStatus === "closed") return;
    setSending(true);
    setNewMsg("");

    try {
      await addDoc(collection(db, "chats", chatId, "messages"), {
        text,
        sender: "customer",
        createdAt: serverTimestamp(),
      });

      await updateDoc(doc(db, "chats", chatId), {
        lastMessage: text,
        lastMessageAt: serverTimestamp(),
        adminUnread: increment(1),
      });
    } catch (err) {
      console.error("Failed to send message:", err);
      setNewMsg(text); // restore message
    }
    setSending(false);
    textareaRef.current?.focus();
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const unreadCount = customerUnread > 0 && !isOpen ? customerUnread : 0;

  return (
    <div className="chat-fab">
      {/* ── CHAT PANEL ── */}
      {isOpen && (
        <div className="chat-panel" role="dialog" aria-label="Live chat">

          {/* Header */}
          <div className="chat-header">
            <div className="chat-header-avatar"><ChatIcon /></div>
            <div className="chat-header-info">
              <div className="chat-header-name">Mr Truck Support</div>
              <div className="chat-header-status">
                <span className={`chat-status-dot ${adminOnline ? "online" : "offline"}`} />
                {chatStatus === "closed" ? "Chat closed" : adminOnline ? "We are online" : "We are offline"}
              </div>
            </div>
            <button
              className="chat-close-btn"
              onClick={() => setIsOpen(false)}
              aria-label="Close chat"
            >
              <CloseIcon />
            </button>
          </div>

          {/* ── WELCOME STEP ── */}
          {step === "welcome" && (
            <div className="chat-welcome">
              <div className="chat-welcome-intro">
                <h3>How can we help?</h3>
                <p>
                  Fill in your name to start chatting with our support team.
                  We typically respond within a few minutes.
                </p>
              </div>

              <form onSubmit={handleStart}>
                <div className="chat-form-group">
                  <label htmlFor="chat-name">Your Name *</label>
                  <input
                    id="chat-name"
                    className="chat-input-field"
                    type="text"
                    placeholder="e.g. John Kamau"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                    autoFocus
                  />
                </div>

                <div className="chat-form-group">
                  <label htmlFor="chat-email">Email (optional)</label>
                  <input
                    id="chat-email"
                    className="chat-input-field"
                    type="email"
                    placeholder="john@company.co.ke"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                </div>

                <button
                  type="submit"
                  className="chat-start-btn"
                  disabled={starting || !name.trim()}
                >
                  {starting ? "Starting…" : "Start Chat →"}
                </button>
              </form>
            </div>
          )}

          {/* ── CHAT STEP ── */}
          {step === "chat" && (
            <>
              <div className="chat-messages">
                <div className="chat-greeting">
                  Chat started — we'll reply shortly
                </div>

                {messages.map((msg) => (
                  <div
                    key={msg.id}
                    className={`chat-msg chat-msg--${msg.sender}`}
                  >
                    {msg.sender === "admin" && (
                      <span className="chat-msg-sender">Support</span>
                    )}
                    <div className={`chat-bubble chat-bubble--${msg.sender}`}>
                      {msg.text}
                    </div>
                    <span className="chat-msg-time">
                      {formatTime(msg.createdAt)}
                    </span>
                  </div>
                ))}

                <div ref={messagesEndRef} />
              </div>

              {chatStatus === "closed" ? (
                <div className="chat-closed-notice">
                  This chat has been closed. <br />
                  <a href="mailto:support@mrtruck.co.ke" style={{ color: "var(--brand)" }}>
                    Email us at support@mrtruck.co.ke
                  </a>
                </div>
              ) : (
                <div className="chat-input-bar">
                  <textarea
                    ref={textareaRef}
                    className="chat-textarea"
                    placeholder="Type a message…"
                    rows={1}
                    value={newMsg}
                    onChange={(e) => setNewMsg(e.target.value)}
                    onKeyDown={handleKeyDown}
                  />
                  <button
                    className="chat-send-btn"
                    onClick={handleSend}
                    disabled={!newMsg.trim() || sending}
                    aria-label="Send message"
                  >
                    <SendIcon />
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* ── FAB BUTTON ── */}
      <div className={`chat-fab-wrap${isOpen ? " chat-fab-wrap--open" : ""}`}>
        {!isOpen && (
          <span className="chat-fab-label">
            {unreadCount > 0 ? `${unreadCount} new message${unreadCount > 1 ? "s" : ""}` : adminOnline ? "we are online" : "we are offline"}
          </span>
        )}
        <button
          className="chat-fab-btn"
          onClick={() => setIsOpen((o) => !o)}
          aria-label={isOpen ? "Close chat" : "Open support chat"}
        >
          {isOpen ? <CloseIcon /> : <ChatIcon />}
          {unreadCount > 0 && (
            <span className="chat-fab-badge">
              {unreadCount > 9 ? "9+" : unreadCount}
            </span>
          )}
        </button>
      </div>
    </div>
  );
}
